import React from 'react';
import { ChevronRight, MessageSquare } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { formatDistanceToNow } from 'date-fns';

interface ReplyingUser {
  username: string;
  avatar?: string;
}

interface ThreadPreviewProps {
  replyCount: number;
  latestReplyTime?: string;
  replyingUsers: ReplyingUser[];
  onExpand: () => void;
}

const ThreadPreview = ({ 
  replyCount, 
  latestReplyTime, 
  replyingUsers, 
  onExpand 
}: ThreadPreviewProps) => {
  const displayUsers = replyingUsers.slice(0, 3);
  const remainingCount = Math.max(0, replyCount - 3);

  return (
    <div
      onClick={onExpand}
      className="ml-12 mt-1 mb-1 px-3 py-2 bg-accent/50 hover:bg-accent/70 border-l-2 border-primary rounded-md cursor-pointer transition-colors group"
    >
      <div className="flex items-center gap-2 text-sm">
        {/* User avatars */}
        <div className="flex -space-x-2">
          {displayUsers.map((user, idx) => (
            <Avatar key={idx} className="h-6 w-6 border-2 border-background">
              <AvatarFallback className="text-xs bg-primary/20">
                {user.username.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          ))}
          {remainingCount > 0 && (
            <div className="h-6 w-6 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs font-medium">
              +{remainingCount}
            </div>
          )}
        </div>

        {/* Reply count */}
        <div className="flex items-center gap-1 text-primary font-medium">
          <MessageSquare className="h-3.5 w-3.5" />
          <span>{replyCount} {replyCount === 1 ? 'reply' : 'replies'}</span>
        </div>

        {/* Last reply time */}
        {latestReplyTime && (
          <span className="text-xs text-muted-foreground">
            Last reply {formatDistanceToNow(new Date(latestReplyTime), { addSuffix: true })}
          </span>
        )}

        {/* Expand indicator */}
        <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
    </div>
  );
};

export default ThreadPreview;
